import { Injectable } from '@angular/core';
import { TokenPack } from '@mira-avant/mtlibs';

@Injectable({
	providedIn: 'root',
})
export class LocalStorageService {
	private readonly TOKEN_KEY = 'auth_token';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';
	private readonly USER_KEY = 'user_info';
	private readonly TOKEN_PACK_KEY = 'token_pack';
	private readonly LOGIN_DESTINATION_KEY = 'login_destination';
	private readonly CMS_TABLE_PAGE_SIZES_KEY = 'cms_table_page_sizes';

	// Token Pack methods
	getTokenPack(): TokenPack | null {
		const tokenPack = localStorage.getItem(this.TOKEN_PACK_KEY);
		if (tokenPack === null) {
			return null;
		}

		const parsedPack = JSON.parse(tokenPack);
		if (parsedPack.tokenExpirationDate) {
			parsedPack.tokenExpirationDate = new Date(parsedPack.tokenExpirationDate);
		}
		return parsedPack;
	}

	setTokenPack(tokenPack: TokenPack): void {
		localStorage.setItem(this.TOKEN_PACK_KEY, JSON.stringify(tokenPack));

		// Set individual components for backward compatibility
		if (tokenPack.token) {
			this.setToken(tokenPack.token);
		}
		if (tokenPack.refreshToken) {
			this.setRefreshToken(tokenPack.refreshToken);
		}
		if (tokenPack.cognitoUser) {
			this.setUserInfo(tokenPack.cognitoUser);
		}
	}

	removeTokenPack(): void {
		localStorage.removeItem(this.TOKEN_PACK_KEY);
	}

	getToken(): string | null {
		return localStorage.getItem(this.TOKEN_KEY);
	}

	setToken(token: string): void {
		localStorage.setItem(this.TOKEN_KEY, token);
	}

	removeToken(): void {
		localStorage.removeItem(this.TOKEN_KEY);
	}

	getRefreshToken(): string | null {
		return localStorage.getItem(this.REFRESH_TOKEN_KEY);
	}

	setRefreshToken(token: string): void {
		localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
	}

	removeRefreshToken(): void {
		localStorage.removeItem(this.REFRESH_TOKEN_KEY);
	}

	getUserInfo<T>(): T | null {
		const userInfo = localStorage.getItem(this.USER_KEY);
		return userInfo ? JSON.parse(userInfo) : null;
	}

	setUserInfo<T>(user: T): void {
		localStorage.setItem(this.USER_KEY, JSON.stringify(user));
	}

	removeUserInfo(): void {
		localStorage.removeItem(this.USER_KEY);
	}

	/**
	 * Save desired login destination
	 *
	 * @public
	 * @param {string} destination
	 */
	public saveLoginDestination(destination: string): void {
		localStorage.setItem(this.LOGIN_DESTINATION_KEY, destination);
	}

	/**
	 * Retrieve the route parts present prior to attempted login.
	 *
	 * @public
	 * @param {boolean} [andClear=true]
	 * @returns {string}
	 */
	public getLoginDestination(andClear: boolean = true): string | undefined {
		const storedParts = localStorage.getItem(this.LOGIN_DESTINATION_KEY);
		const destination: string | undefined = storedParts ? storedParts : undefined;

		if (andClear) {
			this.clearLoginDestination();
		}

		return destination;
	}

	/**
	 * Clear the destination present prior to attempted login.
	 *
	 * @public
	 */
	public clearLoginDestination(): void {
		localStorage.removeItem(this.LOGIN_DESTINATION_KEY);
	}

	clearAllAuthData(): void {
		this.removeToken();
		this.removeRefreshToken();
		this.removeUserInfo();
		this.removeTokenPack();
	}

	public getCmsTablePageSizes(): Record<string, number> {
		const pageSizesJson = localStorage.getItem(this.CMS_TABLE_PAGE_SIZES_KEY);
		if (!pageSizesJson) {
			return {};
		}

		const parsed = JSON.parse(pageSizesJson) as Record<string, unknown>;
		const normalized: Record<string, number> = {};

		Object.entries(parsed).forEach(([tableKey, pageSize]) => {
			if (Number.isInteger(pageSize) && (pageSize as number) > 0) {
				normalized[tableKey] = pageSize as number;
			}
		});

		return normalized;
	}

	public setCmsTablePageSizes(pageSizes: Record<string, number>): void {
		localStorage.setItem(this.CMS_TABLE_PAGE_SIZES_KEY, JSON.stringify(pageSizes));
	}
}
